import type { ReactNode } from "react";
import { useTranslation } from "../i18n/useTranslation";
import { translate, type Locale } from "../i18n";

/**
 * A LEGAL LINK: Terms of Service, Privacy Policy.
 *
 * Opens in a new tab so the person does not lose a booking, a code screen or
 * a half-filled account form to read a document, and says so to screen
 * readers after the link text (" (opens in a new tab)"), in the page's
 * language. `rel="noopener noreferrer"`: the opened document gets no handle
 * on this tab, and no Referer naming the page it came from (security audit
 * WEB-TOS-04).
 *
 * The note uses Book's existing screen-reader-only class, `.b-visually-hidden`
 * (index.css). The link's look comes from wherever it sits (`.b-app a`,
 * `.b-footer a`): underlined or in the action colour, never plain text.
 */
export function NewTabLink({
  href,
  children,
  testId,
  className,
  locale,
}: {
  href: string;
  children: ReactNode;
  testId?: string;
  className?: string;
  locale?: Locale;
}) {
  const { t } = useTranslation();
  const note = locale ? translate(locale, "link.newTab") : t("link.newTab");
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={className ? `b-newtab ${className}` : "b-newtab"}
    >
      {children}
      <span className="b-visually-hidden">{note}</span>
    </a>
  );
}

/**
 * A sentence with ONE bracketed link in it, e.g. "I'm 18 or older and agree to
 * Lura's [Terms of Service]." The whole sentence is one catalogue string per
 * locale, so a translation can put the link wherever its grammar needs it. A string with no
 * brackets renders as plain text rather than inventing a link.
 */
export function LinkedSentence({
  text,
  href,
  testId,
  locale,
  renderLink,
}: {
  text: string;
  href?: string;
  testId?: string;
  /** The sentence's language, when it is not the page's. */
  locale?: Locale;
  /** The bracketed words as some other control (rc.10: an in-page toggle). */
  renderLink?: (label: string) => ReactNode;
}) {
  const match = /^(.*?)\[([^\]]+)\](.*)$/s.exec(text);
  if (!match) return <>{text}</>;
  const [, before, label, after] = match;
  return (
    <>
      {before}
      {renderLink ? renderLink(label ?? "") : (
        <NewTabLink href={href ?? ""} testId={testId} locale={locale}>
          {label}
        </NewTabLink>
      )}
      {after}
    </>
  );
}

/** The sentence as plain text: the brackets removed, the words kept. */
export function plainSentence(text: string): string {
  return text.replace(/\[([^\]]+)\]/g, "$1");
}
